import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5555";
//const API_BASE_URL = "http://localhost:5555";
const SelectedCar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // =========================================================
  // BOOKING DATA
  // =========================================================

  const searchParams = new URLSearchParams(location.search);

  const carId =
    searchParams.get("carmodel_id") ||
    searchParams.get("car_id") ||
    searchParams.get("vehicle_id");

  const pickupLocation =
    searchParams.get("cmbPickLocation") ||
    searchParams.get("pickupLocation") ||
    "";

  const dropLocation =
    searchParams.get("cmbDropLocation") ||
    searchParams.get("dropLocation") ||
    "";

  const pickupDate =
    searchParams.get("pickdate") ||
    searchParams.get("pickupDate") ||
    "";

  const returnDate =
    searchParams.get("returndate") ||
    searchParams.get("returnDate") ||
    "";

  // =========================================================
  // STATES
  // =========================================================

  const [car, setCar] = useState(null);

  const [locations, setLocations] = useState([]);

  const [optionalExtras, setOptionalExtras] = useState([]);

  const [insurance, setInsurance] = useState([]);

  const [selectedExtras, setSelectedExtras] = useState({});

  const [selectedInsurance, setSelectedInsurance] = useState({});

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  // =========================================================
  // DATE / DAYS
  // =========================================================

  const calculateDays = () => {
    if (!pickupDate || !returnDate) {
      return 1;
    }

    const start = new Date(pickupDate);
    const end = new Date(returnDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 1;
    }

    const difference = end.getTime() - start.getTime();

    const days = Math.ceil(difference / (1000 * 60 * 60 * 24));

    return days > 0 ? days : 1;
  };

  const rentalDays = calculateDays();

  // =========================================================
  // FETCH DATA
  // =========================================================

  useEffect(() => {
    fetchBookingData();
  }, [carId]);

  const fetchBookingData = async () => {
    try {
      setLoading(true);
      setError("");

      // -----------------------------------------------------
      // GET CAR
      // -----------------------------------------------------

      let carData = null;

      if (carId) {
        try {
          const carResponse = await fetch(
            `${API_BASE_URL}/api/cars/${carId}`
          );

          if (carResponse.ok) {
            const result = await carResponse.json();

            carData = result.data || result.car || result;
          }
        } catch (err) {
          console.log("Car details API failed:", err);
        }
      }

      // -----------------------------------------------------
      // IF DETAIL API DOES NOT EXIST,
      // GET ALL CARS AND FIND SELECTED CAR
      // -----------------------------------------------------

      if (!carData) {
        try {
          const carsResponse = await fetch(
            `${API_BASE_URL}/api/cars`
          );

          if (carsResponse.ok) {
            const result = await carsResponse.json();

            const cars = result.data || [];

            if (carId) {
              carData = cars.find(
                (item) =>
                  String(
                    item.carmodel_id ||
                      item.car_id ||
                      item.vehicle_id ||
                      item.id
                  ) === String(carId)
              );
            }

            // If no ID was supplied, use first car
            if (!carData && cars.length > 0) {
              carData = cars[0];
            }
          }
        } catch (err) {
          console.log("Cars API failed:", err);
        }
      }

      setCar(carData || null);

      // -----------------------------------------------------
      // LOCATIONS
      // -----------------------------------------------------

      try {
        const locationResponse = await fetch(
          `${API_BASE_URL}/api/locations`
        );

        if (locationResponse.ok) {
          const result = await locationResponse.json();

          setLocations(result.data || []);
        }
      } catch (err) {
        console.log("Locations API not available");
      }

      // -----------------------------------------------------
      // OPTIONAL EXTRAS
      // -----------------------------------------------------

      try {
        const extraResponse = await fetch(
          `${API_BASE_URL}/api/optional-extras`
        );

        if (extraResponse.ok) {
          const result = await extraResponse.json();

          setOptionalExtras(result.data || []);
        }
      } catch (err) {
        console.log("Optional extras API not available");
      }

      // -----------------------------------------------------
      // INSURANCE
      // -----------------------------------------------------

      try {
        const insuranceResponse = await fetch(
          `${API_BASE_URL}/api/insurance`
        );

        if (insuranceResponse.ok) {
          const result = await insuranceResponse.json();

          setInsurance(result.data || []);
        }
      } catch (err) {
        console.log("Insurance API not available");
      }
    } catch (err) {
      console.error(err);

      setError("Unable to load booking details.");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // IMAGE URL
  // =========================================================

  const getCarImage = (image) => {
    if (!image) {
      return "/images/no-car-image.png";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://")
    ) {
      return image;
    }

    if (image.startsWith("/")) {
      return `${API_BASE_URL}${image}`;
    }

    if (image.startsWith("uploads/")) {
      return `${API_BASE_URL}/${image}`;
    }

    if (image.startsWith("images/")) {
      return `${API_BASE_URL}/uploads/cars/${image}`;
    }

    return `${API_BASE_URL}/uploads/cars/${image}`;
  };

  // =========================================================
  // CAR DATA
  // =========================================================

  const carName =
    car?.name ||
    car?.carmodel_name ||
    car?.model_name ||
    car?.model ||
    "Selected Car";

  const carBrand =
    car?.brand_name ||
    car?.brand ||
    car?.carmodel_brand ||
    "";

  const passengers =
    car?.passengers ||
    car?.carmodel_passgrno ||
    car?.passenger ||
    car?.seats ||
    0;

  const luggage =
    car?.luggage ||
    car?.carmodel_luggno ||
    car?.bags ||
    0;

  const doors =
    car?.doors ||
    car?.carmodel_doors ||
    0;

  const airCondition =
    car?.airCondition ||
    car?.air_condition ||
    car?.carmodel_airconditionr ||
    car?.ac ||
    "Yes";

  const carImage =
    car?.image ||
    car?.carmodel_image ||
    car?.car_image ||
    "";

  const pricePerDay = Number(
    car?.pricePerDay ||
      car?.price_per_day ||
      car?.carprice ||
      car?.price ||
      0
  );

  // =========================================================
  // LOCATION CHARGES
  // =========================================================

  const selectedPickupLocation = locations.find(
    (item) =>
      String(item.loc_id || item.id) ===
      String(pickupLocation)
  );

  const selectedDropLocation = locations.find(
    (item) =>
      String(item.loc_id || item.id) ===
      String(dropLocation)
  );

  const pickupCharge = Number(
    selectedPickupLocation?.loc_pickupprice ||
      selectedPickupLocation?.pickup_price ||
      0
  );

  const dropCharge = Number(
    selectedDropLocation?.loc_dropupprice ||
      selectedDropLocation?.drop_price ||
      0
  );

  // =========================================================
  // BASE PRICE
  // =========================================================

  const basePrice = useMemo(() => {
    return pricePerDay * rentalDays;
  }, [pricePerDay, rentalDays]);

  // =========================================================
  // OPTIONAL EXTRAS TOTAL
  // =========================================================

  const extrasTotal = useMemo(() => {
    let total = 0;

    Object.entries(selectedExtras).forEach(
      ([extraId, quantity]) => {
        const extra = optionalExtras.find(
          (item) =>
            String(
              item.optdet_id ||
                item.extra_id ||
                item.id
            ) === String(extraId)
        );

        if (!extra) return;

        const dailyRate = Number(
          extra.opt_dayrate ||
            extra.day_rate ||
            extra.price ||
            0
        );

        const maxRate = Number(
          extra.opt_maxrate ||
            extra.max_rate ||
            0
        );

        let rate = dailyRate * rentalDays;

        if (maxRate > 0 && rate > maxRate) {
          rate = maxRate;
        }

        total += rate * Number(quantity || 1);
      }
    );

    return total;
  }, [selectedExtras, optionalExtras, rentalDays]);

  // =========================================================
  // INSURANCE TOTAL
  // =========================================================

  const insuranceTotal = useMemo(() => {
    let total = 0;

    Object.entries(selectedInsurance).forEach(
      ([insuranceId, selected]) => {
        if (!selected) return;

        const item = insurance.find(
          (insuranceItem) =>
            String(
              insuranceItem.insdtl_id ||
                insuranceItem.insurance_id ||
                insuranceItem.id
            ) === String(insuranceId)
        );

        if (!item) return;

        const dailyRate = Number(
          item.insdtl_chargeperday ||
            item.charge_per_day ||
            item.price ||
            0
        );

        total += dailyRate * rentalDays;
      }
    );

    return total;
  }, [selectedInsurance, insurance, rentalDays]);

  // =========================================================
  // FINAL TOTAL
  // =========================================================

  const estimatedTotal = useMemo(() => {
    return (
      basePrice +
      pickupCharge +
      dropCharge +
      extrasTotal +
      insuranceTotal
    );
  }, [
    basePrice,
    pickupCharge,
    dropCharge,
    extrasTotal,
    insuranceTotal,
  ]);

  // =========================================================
  // EXTRA SELECT
  // =========================================================

  const handleExtraQuantity = (id, quantity) => {
    setSelectedExtras((previous) => ({
      ...previous,
      [id]: Number(quantity),
    }));
  };

  // =========================================================
  // EXTRA CHECKBOX
  // =========================================================

  const handleExtraCheck = (id) => {
    setSelectedExtras((previous) => {
      const updated = { ...previous };

      if (updated[id]) {
        delete updated[id];
      } else {
        updated[id] = 1;
      }

      return updated;
    });
  };

  // =========================================================
  // INSURANCE CHECKBOX
  // =========================================================

  const handleInsuranceCheck = (id) => {
    setSelectedInsurance((previous) => ({
      ...previous,
      [id]: !previous[id],
    }));
  };

  // =========================================================
  // NEXT BUTTON
  // =========================================================

  const handleNext = () => {
    const bookingData = {
      carId:
        car?.carmodel_id ||
        car?.car_id ||
        car?.vehicle_id ||
        car?.id,

      carName,

      pickupLocation,

      dropLocation,

      pickupDate,

      returnDate,

      rentalDays,

      pricePerDay,

      basePrice,

      pickupCharge,

      dropCharge,

      extras: selectedExtras,

      insurance: selectedInsurance,

      extrasTotal,

      insuranceTotal,

      estimatedTotal,
    };

    console.log(
      "BOOKING DATA:",
      bookingData
    );

    // Store temporarily
    sessionStorage.setItem(
      "bookingData",
      JSON.stringify(bookingData)
    );

    navigate("/bookacarfour");
  };

  // =========================================================
  // BACK
  // =========================================================

  const handleBack = () => {
    navigate(-1);
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "100px 20px",
        }}
      >
        <h4>Loading booking details...</h4>
      </div>
    );
  }

  // =========================================================
  // ERROR
  // =========================================================

  if (error) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "100px 20px",
        }}
      >
        <h4>{error}</h4>

        <button
          className="btn btn-primary mt-3"
          onClick={fetchBookingData}
        >
          Try Again
        </button>
      </div>
    );
  }

  // =========================================================
  // NO CAR
  // =========================================================

  if (!car) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "100px 20px",
        }}
      >
        <h4>Car not found</h4>

        <button
          className="btn btn-primary mt-3"
          onClick={handleBack}
        >
          ← Back
        </button>
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="selected-car-page">

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div className="container py-4">

        {/* ===================================================
            PAGE TITLE
        ==================================================== */}

        <div className="mb-4">

          <h3 className="fw-bold">
            Car Booking
          </h3>

          <div
            style={{
              borderBottom:
                "2px solid #eee",
              marginTop: "10px",
            }}
          />

        </div>

        {/* ===================================================
            CAR SECTION
        ==================================================== */}

        <div className="card shadow-sm mb-4">

          <div className="card-body">

            <div className="row">

              {/* CAR INFORMATION */}

              <div className="col-lg-8">

                <h3 className="fw-bold mb-1">
                  {carName}
                </h3>

                <div
                  style={{
                    color: "#777",
                    marginBottom: "20px",
                  }}
                >
                  {carBrand}
                </div>

                <div className="row">

                  {/* IMAGE */}

                  <div className="col-md-5">

                    <div
                      style={{
                        textAlign: "center",
                      }}
                    >

                      <img
                        src={getCarImage(
                          carImage
                        )}
                        alt={carName}
                        style={{
                          width: "100%",
                          maxWidth: "300px",
                          height: "200px",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.currentTarget.src =
                            "/images/no-car-image.png";
                        }}
                      />

                    </div>

                  </div>

                  {/* DETAILS */}

                  <div className="col-md-7">

                    <CarDetail
                      icon="👤"
                      title="Passengers"
                      value={passengers}
                    />

                    <CarDetail
                      icon="🧳"
                      title="Luggage"
                      value={luggage}
                    />

                    <CarDetail
                      icon="🚪"
                      title="Doors"
                      value={doors}
                    />

                    <CarDetail
                      icon="❄️"
                      title="Air Conditioning"
                      value={airCondition}
                    />

                    <button
                      className="btn btn-outline-primary mt-3"
                      onClick={() =>
                        alert(
                          "Car details popup can be connected to your car details API."
                        )
                      }
                    >
                      Car Details
                    </button>

                  </div>

                </div>

              </div>

              {/* PRICE SUMMARY */}

              <div className="col-lg-4">

                <div
                  className="card"
                  style={{
                    background:
                      "#f8f8f8",
                    border: "1px solid #ddd",
                  }}
                >

                  <div className="card-body">

                    <h5 className="fw-bold">
                      Price Summary
                    </h5>

                    <hr />

                    <PriceRow
                      title="Rental Duration"
                      value={`${rentalDays} day${
                        rentalDays > 1
                          ? "s"
                          : ""
                      }`}
                    />

                    <PriceRow
                      title="Car Price"
                      value={`${pricePerDay.toFixed(
                        2
                      )} × ${rentalDays}`}
                    />

                    <PriceRow
                      title="Base Price"
                      value={basePrice}
                    />

                    {pickupCharge > 0 && (
                      <PriceRow
                        title="Pickup Charge"
                        value={pickupCharge}
                      />
                    )}

                    {dropCharge > 0 && (
                      <PriceRow
                        title="Drop-off Charge"
                        value={dropCharge}
                      />
                    )}

                    {extrasTotal > 0 && (
                      <PriceRow
                        title="Optional Extras"
                        value={extrasTotal}
                      />
                    )}

                    {insuranceTotal > 0 && (
                      <PriceRow
                        title="Insurance"
                        value={insuranceTotal}
                      />
                    )}

                    <hr />

                    <div
                      className="d-flex justify-content-between"
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                      }}
                    >

                      <span>
                        Estimated Total
                      </span>

                      <span>
                        €{" "}
                        {estimatedTotal.toFixed(
                          2
                        )}
                      </span>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

        {/* ===================================================
            OUR PRICES INCLUDE
        ==================================================== */}

        <div className="card shadow-sm mb-4">

          <div className="card-body">

            <h5 className="fw-bold mb-3">
              Our Prices Includes
            </h5>

            <ul className="mb-0">

              <li>Basic rental charges</li>

              <li>Standard vehicle equipment</li>

              <li>Customer support</li>

              <li>Taxes according to booking conditions</li>

            </ul>

          </div>

        </div>

        {/* ===================================================
            OPTIONAL EXTRAS
        ==================================================== */}

        {optionalExtras.length > 0 && (
          <div className="card shadow-sm mb-4">

            <div className="card-body">

              <h4 className="fw-bold mb-4">
                Optional Extras
              </h4>

              <div
                className="table-responsive"
              >

                <table className="table table-bordered">

                  <thead>

                    <tr>

                      <th>
                        Extra
                      </th>

                      <th>
                        Price
                      </th>

                      <th>
                        Quantity
                      </th>

                      <th>
                        Select
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {optionalExtras.map(
                      (extra) => {

                        const id =
                          extra.optdet_id ||
                          extra.extra_id ||
                          extra.id;

                        const name =
                          extra.optitem_name ||
                          extra.name ||
                          extra.extra_name ||
                          "Optional Extra";

                        const price =
                          Number(
                            extra.opt_dayrate ||
                              extra.day_rate ||
                              extra.price ||
                              0
                          );

                        const max =
                          Number(
                            extra.opt_max ||
                              extra.max_quantity ||
                              1
                          );

                        return (
                          <tr key={id}>

                            <td>
                              {name}
                            </td>

                            <td>
                              €{" "}
                              {price.toFixed(
                                2
                              )}{" "}
                              / day
                            </td>

                            <td>

                              <select
                                className="form-select"
                                value={
                                  selectedExtras[
                                    id
                                  ] || 1
                                }
                                onChange={(e) =>
                                  handleExtraQuantity(
                                    id,
                                    e.target
                                      .value
                                  )
                                }
                                disabled={
                                  !selectedExtras[
                                    id
                                  ]
                                }
                                style={{
                                  width: "100px",
                                }}
                              >

                                {Array.from(
                                  {
                                    length:
                                      max,
                                  },
                                  (
                                    _,
                                    index
                                  ) => (
                                    <option
                                      key={
                                        index +
                                        1
                                      }
                                      value={
                                        index +
                                        1
                                      }
                                    >
                                      {index +
                                        1}
                                    </option>
                                  )
                                )}

                              </select>

                            </td>

                            <td
                              className="text-center"
                            >

                              <input
                                type="checkbox"
                                checked={
                                  !!selectedExtras[
                                    id
                                  ]
                                }
                                onChange={() =>
                                  handleExtraCheck(
                                    id
                                  )
                                }
                              />

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>

            </div>

          </div>
        )}

        {/* ===================================================
            INSURANCE
        ==================================================== */}

        {insurance.length > 0 && (
          <div className="card shadow-sm mb-4">

            <div className="card-body">

              <h4 className="fw-bold mb-4">
                Insurance
              </h4>

              <div
                className="table-responsive"
              >

                <table className="table table-bordered">

                  <thead>

                    <tr>

                      <th>
                        Insurance
                      </th>

                      <th>
                        Price / Day
                      </th>

                      <th>
                        Total
                      </th>

                      <th>
                        Select
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {insurance.map(
                      (item) => {

                        const id =
                          item.insdtl_id ||
                          item.insurance_id ||
                          item.id;

                        const name =
                          item.insitem_name ||
                          item.name ||
                          "Insurance";

                        const price =
                          Number(
                            item.insdtl_chargeperday ||
                              item.charge_per_day ||
                              item.price ||
                              0
                          );

                        return (
                          <tr key={id}>

                            <td>
                              {name}
                            </td>

                            <td>
                              €{" "}
                              {price.toFixed(
                                2
                              )}
                            </td>

                            <td>
                              €{" "}
                              {(
                                price *
                                rentalDays
                              ).toFixed(
                                2
                              )}
                            </td>

                            <td className="text-center">

                              <input
                                type="checkbox"
                                checked={
                                  !!selectedInsurance[
                                    id
                                  ]
                                }
                                onChange={() =>
                                  handleInsuranceCheck(
                                    id
                                  )
                                }
                              />

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>

            </div>

          </div>
        )}

        {/* ===================================================
            FINAL TOTAL
        ==================================================== */}

        <div className="card shadow-sm mb-4">

          <div className="card-body">

            <div className="row align-items-center">

              <div className="col-md-6">

                <h5 className="mb-1">
                  Estimated Total
                </h5>

                <small
                  style={{
                    color: "#777",
                  }}
                >
                  Final amount based on
                  selected options
                </small>

              </div>

              <div
                className="col-md-6 text-md-end"
              >

                <span
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                  }}
                >
                  €{" "}
                  {estimatedTotal.toFixed(
                    2
                  )}
                </span>

              </div>

            </div>

          </div>

        </div>

        {/* ===================================================
            AGE WARNING
        ==================================================== */}

        <div
          style={{
            color: "#333",
            fontSize: "13px",
            backgroundColor:
              "#faf165",
            borderTop:
              "1px solid #000",
            padding: "8px",
            marginBottom: "20px",
          }}
        >

          <strong>
            ** Important :
          </strong>{" "}

          Please make sure the driver
          meets the required minimum age
          and driving licence conditions
          before completing the booking.

        </div>

        {/* ===================================================
            BUTTONS
        ==================================================== */}

        <div
          className="d-flex justify-content-between flex-wrap gap-2"
        >

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleBack}
          >
            « Back
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
          >
            Next »
          </button>

        </div>

      </div>

    </div>
  );
};

// ============================================================
// CAR DETAIL COMPONENT
// ============================================================

const CarDetail = ({
  icon,
  title,
  value,
}) => {
  return (
    <div
      className="d-flex align-items-center mb-3"
    >

      <div
        style={{
          width: "35px",
          fontSize: "20px",
        }}
      >
        {icon}
      </div>

      <div>
        <strong>
          {title}
        </strong>

        <span
          style={{
            marginLeft: "8px",
            color: "#555",
          }}
        >
          : {value}
        </span>
      </div>

    </div>
  );
};

// ============================================================
// PRICE ROW
// ============================================================

const PriceRow = ({
  title,
  value,
}) => {
  const formattedValue =
    typeof value === "number"
      ? `€ ${value.toFixed(2)}`
      : value;

  return (
    <div
      className="d-flex justify-content-between mb-2"
    >

      <span>
        {title}
      </span>

      <span>
        {formattedValue}
      </span>

    </div>
  );
};

export default SelectedCar;